import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import type { TippingComponentConfig } from '../../types';
import { MIN_TIP_AMOUNT } from '../../../utils/tipFee';

interface TippingAmountSelectorProps {
  amount?: number;
  amountLabel: string;
  config: TippingComponentConfig;
  customAmount?: string;
  customAmountBelowMinimum: boolean;
  onChange: (nextValue: { amount?: number; customAmount?: string }) => void;
}

const TippingAmountSelector: React.FC<TippingAmountSelectorProps> = ({
  amount,
  amountLabel,
  config,
  customAmount,
  customAmountBelowMinimum,
  onChange,
}) => {
  const isVertical = config.layout === 'vertical';
  const style = config.style ?? 'tiles';
  const buttonHeight = style === 'tiles' ? 70 : style === 'pills' ? 44 : 40;
  const buttonRadius = style === 'pills' ? '999px' : style === 'compact' ? '4px' : '3px';
  const buttonFontSize = style === 'tiles' ? 22 : style === 'pills' ? 16 : 14;
  const columnCount = Math.max(1, Math.min(config.amounts.length, 3));

  return (
  <Stack spacing={1.5}>
    <Typography
      component="span"
      sx={{
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1,
      }}
    >
      Selected tip amount: {amountLabel}
    </Typography>

    <Box
      sx={{
        display: 'grid',
        gap: style === 'compact' ? 0.75 : 1,
        gridTemplateColumns: isVertical
          ? 'minmax(0, 1fr)'
          : `repeat(${columnCount}, minmax(0, 1fr))`,
      }}
    >
      {config.amounts.map((tipAmount) => {
        const selected = amount === tipAmount.value && !customAmount;

        return (
          <Button
            aria-pressed={selected}
            key={tipAmount.value}
            onClick={() => onChange({ amount: tipAmount.value, customAmount: '' })}
            style={
              {
                '--cx-tip-amount-bg': selected
                  ? config.selectedBackgroundColor ?? '#1C252E'
                  : config.buttonBackgroundColor ?? '#F4F6F8',
                '--cx-tip-amount-border': selected
                  ? config.selectedBorderColor ?? '#1C252E'
                  : config.buttonBorderColor ?? 'rgba(145, 158, 171, 0.20)',
                '--cx-tip-amount-color': selected
                  ? config.selectedTextColor ?? '#FFFFFF'
                  : config.buttonTextColor ?? 'var(--cx-brand-button-text)',
                borderRadius: buttonRadius,
                minHeight: `${buttonHeight}px`,
              } as React.CSSProperties
            }
            sx={{
              bgcolor: 'var(--cx-tip-amount-bg)',
              border: '1px solid',
              borderColor: 'var(--cx-tip-amount-border)',
              color: 'var(--cx-tip-amount-color)',
              borderRadius: buttonRadius,
              fontSize: buttonFontSize,
              fontWeight: 900,
              height: buttonHeight,
              minHeight: buttonHeight,
              transition: selected ? 'none' : undefined,
              '&:hover': {
                bgcolor: selected
                  ? 'var(--cx-tip-amount-bg)'
                  : 'var(--cx-brand-primary-soft)',
                borderColor: 'var(--cx-tip-amount-border)',
              },
            }}
            type="button"
            variant="outlined"
          >
            {tipAmount.label}
          </Button>
        );
      })}
    </Box>

    {config.allowCustomAmount !== false ? (
      <>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <Box sx={{ bgcolor: 'var(--cx-brand-border)', flex: 1, height: '1px' }} />
          <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)' }}>
            or
          </Typography>
          <Box sx={{ bgcolor: 'var(--cx-brand-border)', flex: 1, height: '1px' }} />
        </Box>
        <TextField
          fullWidth
          error={customAmountBelowMinimum}
          helperText={customAmountBelowMinimum ? 'Minimum is $1.00' : undefined}
          onChange={(event) => onChange({ amount: undefined, customAmount: event.target.value })}
          placeholder="Enter amount"
          sx={{
            '& .MuiInputBase-root': {
              bgcolor: 'var(--cx-brand-surface)',
              color: 'var(--cx-brand-text)',
              minHeight: 58,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--cx-brand-border)',
            },
          }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            },
            htmlInput: {
              'aria-label': 'Other tip amount',
              inputMode: 'decimal',
              min: MIN_TIP_AMOUNT,
              step: '0.01',
            },
          }}
          type="number"
          value={customAmount ?? ''}
        />
      </>
    ) : null}
  </Stack>
  );
};

export default TippingAmountSelector;
